<script setup lang="ts">
import { Check as CheckIcon, X as XIcon } from '@lucide/vue'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

import BaseInput from '@/components/ui/BaseInput.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  isMobileRenameSheetOpen,
  MOBILE_RENAME_REQUEST_EVENT,
  type MobileRenameRequest,
} from '@/services/mobileRenameService'

defineOptions({ name: 'MobileRenameActionSheetHost' })

const request = ref<MobileRenameRequest | null>(null)
const draftName = ref('')
const errorMessage = ref('')
const submitting = ref(false)

const open = computed(() => request.value !== null)
const trimmedName = computed(() => draftName.value.trim())

const focusInput = async () => {
  await nextTick()
  const input = document.getElementById('mobile-rename-name')
  if (input instanceof HTMLInputElement) {
    input.focus({ preventScroll: true })
  }
}

const handleRequest = (event: Event) => {
  const detail = (event as CustomEvent<MobileRenameRequest>).detail
  if (!detail) return

  request.value = detail
  isMobileRenameSheetOpen.value = true
  draftName.value = detail.currentName
  errorMessage.value = ''
  submitting.value = false
  void focusInput()
}

const close = () => {
  if (submitting.value) return
  request.value = null
  isMobileRenameSheetOpen.value = false
  draftName.value = ''
  errorMessage.value = ''
}

const submitRename = async () => {
  const activeRequest = request.value
  if (!activeRequest || submitting.value) return

  if (!trimmedName.value) {
    errorMessage.value = 'Name cannot be empty'
    void focusInput()
    return
  }

  errorMessage.value = ''
  submitting.value = true
  try {
    await activeRequest.submit(trimmedName.value)
    request.value = null
    isMobileRenameSheetOpen.value = false
    draftName.value = ''
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to rename item from mobile action sheet', {
      error,
      title: activeRequest.title,
    })
    errorMessage.value = `Failed to rename: ${error instanceof Error ? error.message : String(error)}`
  } finally {
    submitting.value = false
  }
}

onMounted(() => window.addEventListener(MOBILE_RENAME_REQUEST_EVENT, handleRequest))
onUnmounted(() => {
  window.removeEventListener(MOBILE_RENAME_REQUEST_EVENT, handleRequest)
  isMobileRenameSheetOpen.value = false
})
</script>

<template>
  <MobileActionSheet :open="open" :title="request?.title ?? 'Rename'" @close="close">
    <template #header-left>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 disabled:opacity-60"
        :disabled="submitting"
        aria-label="Cancel rename"
        @click="close"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </template>

    <template #header-right>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
        :disabled="submitting || !trimmedName"
        aria-label="Confirm rename"
        @click="submitRename"
      >
        <CheckIcon class="h-4 w-4" />
      </button>
    </template>

    <form class="px-4 pb-4 pt-3" @submit.prevent="submitRename">
      <BaseInput
        id="mobile-rename-name"
        v-model="draftName"
        :invalid="!!errorMessage"
        :disabled="submitting"
        autocomplete="off"
        enterkeyhint="done"
        aria-label="New name"
        :aria-invalid="errorMessage ? 'true' : 'false'"
        :aria-describedby="errorMessage ? 'mobile-rename-error' : undefined"
        @input="errorMessage = ''"
      />
      <p
        v-if="errorMessage"
        id="mobile-rename-error"
        class="mt-1.5 text-sm text-danger-700"
        role="alert"
      >
        {{ errorMessage }}
      </p>
    </form>
  </MobileActionSheet>
</template>
